/**
 * Video Module
 * Swaps the poster of a video block for a YouTube iframe on the first click.
 *
 * Nothing is requested from YouTube until then, which keeps the block off the
 * critical path of any page it sits on.
 */

const PLAYER_SELECTOR = '.video__player';

/**
 * Builds the YouTube embed URL for a player
 * @param {HTMLElement} player - The player container element
 * @returns {string} The embed URL, set to autoplay
 */
function buildEmbedUrl(player) {
  const params = new URLSearchParams({
    autoplay: '1',
    rel: '0',
    modestbranding: '1',
  });

  const start = parseInt(player.dataset.videoStart, 10);
  if (start > 0) {
    params.set('start', String(start));
  }

  return `https://www.youtube-nocookie.com/embed/${player.dataset.videoId}?${params}`;
}

/**
 * Replaces the poster with an autoplaying iframe
 * @param {HTMLElement} player - The player container element
 * @returns {void}
 */
function loadPlayer(player) {
  if (player.classList.contains('video__player--playing')) {
    return;
  }
  player.classList.add('video__player--playing');

  const iframe = document.createElement('iframe');
  iframe.className = 'video__iframe';
  iframe.src = buildEmbedUrl(player);
  iframe.title = player.dataset.videoTitle || 'Video';
  iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
  iframe.allowFullscreen = true;
  iframe.setAttribute('frameborder', '0');

  player.replaceChildren(iframe);
  iframe.focus();
}

/**
 * Wires up every video block on the page
 * @returns {void}
 */
function initVideoBlocks() {
  document.querySelectorAll(PLAYER_SELECTOR).forEach((player) => {
    const button = player.querySelector('.video__play');
    if (button) {
      button.addEventListener('click', () => loadPlayer(player));
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoBlocks);
} else {
  initVideoBlocks();
}

export default initVideoBlocks;
